import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import useAdminReturns from "../../../hooks/useAdminReturns";

/**
 * Process Refund Form component for admin interface
 * Allows admins to select refund method and add notes when processing a return refund
 *
 * @param {Object} props - Component props
 * @param {Object} props.returnDetail - The return being processed
 * @returns {JSX.Element} The ProcessRefundForm component
 */
const ProcessRefundForm = ({ returnDetail }) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns");
  const { processReturnRefund, actionStatus, actionError } = useAdminReturns();

  const [formData, setFormData] = useState({
    refundMethod: "wallet",
    adminNotes: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Calculate the refund amount from returnDetail
  const refundAmount = returnDetail?.refundAmount
    ? parseFloat(returnDetail.refundAmount).toFixed(2)
    : "0.00";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.refundMethod) {
      errors.refundMethod = t(
        "admin.detail.forms.processRefund.errors.methodRequired"
      );
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    processReturnRefund({
      returnId: returnDetail.id,
      refundMethod: formData.refundMethod,
      adminNotes: formData.adminNotes,
    });
  };

  const isLoading = actionStatus === "loading";

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError?.message || tCommon("status.error")}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>
            {t("admin.detail.forms.processRefund.refundAmount")}:{" "}
            <strong>{refundAmount} CHF</strong>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControl
            component="fieldset"
            error={!!formErrors.refundMethod}
            required
          >
            <FormLabel component="legend">
              {t("admin.detail.forms.processRefund.refundMethodLabel")}
            </FormLabel>
            <RadioGroup
              name="refundMethod"
              value={formData.refundMethod}
              onChange={handleInputChange}
            >
              <FormControlLabel
                value="wallet"
                control={<Radio />}
                label={t("admin.detail.forms.processRefund.methods.wallet")}
              />
              {/* <FormControlLabel
                value="original_payment"
                control={<Radio />}
                label="Refund to Original Payment Method"
              /> */}
              <FormControlLabel
                value="manual"
                control={<Radio />}
                label={t("admin.detail.forms.processRefund.methods.manual")}
              />
            </RadioGroup>
            {formErrors.refundMethod && (
              <FormHelperText error>{formErrors.refundMethod}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="adminNotes"
            label={t("admin.detail.forms.processRefund.notesLabel")}
            multiline
            rows={3}
            value={formData.adminNotes}
            onChange={handleInputChange}
            fullWidth
            placeholder={t("admin.detail.forms.processRefund.notesPlaceholder")}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />{" "}
                {tCommon("status.processing")}
              </>
            ) : (
              t("admin.detail.forms.processRefund.submitButton")
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProcessRefundForm;
